import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../swof/api";
import { AdminShell } from "./ProtectedRoute";
import { useAuth } from "./AuthContext";

const knopStijl = {
  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
  color: "var(--gold)", border: "1px solid rgba(201,169,97,0.4)", padding: "10px 16px",
  background: "transparent",
};

const invoerStijl = {
  width: "100%", padding: "12px 14px", background: "rgba(243,234,215,0.04)",
  border: "1px solid var(--line)", fontFamily: "var(--serif)", fontSize: 16,
  color: "var(--cream)", outline: "none",
};

const wegStijl = {
  fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase",
  color: "#e8b0a4", border: "1px solid rgba(201,80,60,0.4)", padding: "8px 12px",
  background: "transparent",
};

function Label({ children }) {
  return <span className="eyebrow" style={{ display: "block", marginBottom: 8, fontSize: 10, color: "var(--gold)" }}>{children}</span>;
}

export default function BeheerdersPage() {
  const { user, herlaadGebruiker } = useAuth();
  const [data, setData] = useState(null);
  const [naam, setNaam] = useState("");
  const [email, setEmail] = useState("");
  const [fout, setFout] = useState("");
  const [melding, setMelding] = useState("");
  const [link, setLink] = useState("");
  const [bezig, setBezig] = useState(false);

  const [mijnNaam, setMijnNaam] = useState("");
  const [mijnEmail, setMijnEmail] = useState("");
  const [huidig, setHuidig] = useState("");
  const [nieuw, setNieuw] = useState("");

  const laad = () => api.get("/admin/beheerders").then(({ data }) => setData(data));

  useEffect(() => {
    document.body.classList.add("swof");
    laad();
    return () => document.body.classList.remove("swof");
  }, []);

  useEffect(() => {
    if (user) { setMijnNaam(user.naam || ""); setMijnEmail(user.email || ""); }
  }, [user]);

  const meld = (e) => setFout(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  const schoon = () => { setFout(""); setMelding(""); setLink(""); };

  const uitnodigen = async (e) => {
    e.preventDefault();
    schoon();
    setBezig(true);
    try {
      const { data: nieuwe } = await api.post("/admin/beheerders", { naam, email });
      setNaam(""); setEmail("");
      setMelding(nieuwe.mail_verstuurd
        ? `Uitnodiging verstuurd naar ${nieuwe.email}.`
        : `Account aangemaakt, maar de e-mail kon niet worden verstuurd. Geef deze link zelf door:`);
      if (nieuwe.link) setLink(nieuwe.link);
      await laad();
    } catch (err) { meld(err); } finally { setBezig(false); }
  };

  const opnieuw = async (b) => {
    schoon();
    setBezig(true);
    try {
      const { data: res } = await api.post(`/admin/beheerders/${b.id}/opnieuw-uitnodigen`);
      setMelding(res.mail_verstuurd
        ? `Nieuwe uitnodiging verstuurd naar ${b.email}.`
        : "De e-mail kon niet worden verstuurd. Geef deze link zelf door:");
      if (res.link) setLink(res.link);
    } catch (err) { meld(err); } finally { setBezig(false); }
  };

  const verwijder = async (b) => {
    if (!window.confirm(`${b.naam || b.email} de toegang ontnemen?\n\nHij of zij kan daarna niet meer inloggen.`)) return;
    schoon();
    setBezig(true);
    try {
      await api.delete(`/admin/beheerders/${b.id}`);
      await laad();
    } catch (err) { meld(err); } finally { setBezig(false); }
  };

  const accountOpslaan = async (e) => {
    e.preventDefault();
    schoon();
    setBezig(true);
    try {
      await api.put("/admin/mijn-account", { naam: mijnNaam, email: mijnEmail });
      await herlaadGebruiker();
      await laad();
      setMelding("Uw naam en e-mailadres zijn opgeslagen. Vanaf nu logt u in met dit adres.");
    } catch (err) { meld(err); } finally { setBezig(false); }
  };

  const wachtwoordOpslaan = async (e) => {
    e.preventDefault();
    schoon();
    setBezig(true);
    try {
      await api.put("/admin/mijn-wachtwoord", { huidig, nieuw });
      setHuidig(""); setNieuw("");
      setMelding("Uw wachtwoord is gewijzigd.");
    } catch (err) { meld(err); } finally { setBezig(false); }
  };

  return (
    <AdminShell title="Beheerders">
      <h1 style={{
        fontFamily: "var(--serif)", fontSize: "clamp(30px, 3.6vw, 46px)", fontWeight: 400,
        margin: "0 0 12px", letterSpacing: "-0.02em",
      }}>Wie mag erbij</h1>
      <p style={{ fontFamily: "var(--sans)", fontSize: 14, opacity: 0.65, margin: "0 0 32px", maxWidth: "64ch" }}>
        Vul naam en e-mailadres in; de nieuwe beheerder krijgt een uitnodiging per e-mail en kiest
        zelf een wachtwoord. Alle beheerders mogen hetzelfde, inclusief het toevoegen en
        verwijderen van collega's.
      </p>

      {fout && (
        <div data-testid="beheerders-fout" style={{
          marginBottom: 20, padding: "12px 14px", border: "1px solid rgba(201,80,60,0.5)",
          background: "rgba(201,80,60,0.08)", fontFamily: "var(--sans)", fontSize: 13, color: "#e8b0a4",
        }}>{fout}</div>
      )}
      {melding && (
        <div data-testid="beheerders-melding" style={{
          marginBottom: 20, padding: "12px 14px", border: "1px solid rgba(201,169,97,0.5)",
          background: "rgba(201,169,97,0.08)", fontFamily: "var(--sans)", fontSize: 13,
        }}>
          {melding}
          {link && (
            <div data-testid="beheerders-link" style={{
              marginTop: 10, fontFamily: "var(--mono)", fontSize: 11, wordBreak: "break-all",
              color: "var(--gold)",
            }}>{link}</div>
          )}
        </div>
      )}

      <form onSubmit={uitnodigen} style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14,
        alignItems: "end", marginBottom: 36,
      }}>
        <label>
          <Label>Naam</Label>
          <input value={naam} onChange={(e) => setNaam(e.target.value)} required
            data-testid="beheerder-naam" style={invoerStijl} placeholder="Voor- en achternaam" />
        </label>
        <label>
          <Label>E-mail</Label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            data-testid="beheerder-email" style={invoerStijl} placeholder="naam@voorbeeld.nl" />
        </label>
        <button type="submit" disabled={bezig} data-testid="beheerder-uitnodigen" style={{
          ...knopStijl, background: "var(--gold)", color: "var(--ink)", border: "none",
          padding: "13px 20px",
        }}>Uitnodigen</button>
      </form>

      {!data ? (
        <div data-testid="beheerders-laden" style={{ opacity: 0.5 }}>Laden…</div>
      ) : (
        <ul data-testid="beheerder-lijst" style={{
          listStyle: "none", padding: 0, margin: 0, borderTop: "1px solid var(--line)",
        }}>
          {data.beheerders.map((b) => (
            <li key={b.id} data-testid={`beheerder-${b.id}`} style={{
              display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center",
              justifyContent: "space-between", padding: "15px 8px",
              borderBottom: "1px solid var(--line)",
            }}>
              <span style={{ fontFamily: "var(--serif)", fontSize: 18, minWidth: 140 }}>
                {b.naam || "—"}{b.ik && <span style={{ color: "var(--gold)", fontSize: 12 }}> · u</span>}
              </span>
              <span style={{ fontFamily: "var(--sans)", fontSize: 13, opacity: 0.75, flex: "1 1 200px", wordBreak: "break-all" }}>{b.email}</span>
              <span className="eyebrow" style={{
                fontSize: 9, color: b.uitgenodigd ? "var(--gold)" : "rgba(243,234,215,0.45)",
              }}>{b.uitgenodigd ? "Uitgenodigd" : "Actief"}</span>
              <span style={{ fontFamily: "var(--mono)", fontSize: 10, opacity: 0.5 }}>
                {b.laatste_login
                  ? `Laatst ingelogd ${new Date(b.laatste_login).toLocaleDateString("nl-NL")}`
                  : "Nog niet ingelogd"}
              </span>
              <span style={{ display: "flex", gap: 8 }}>
                {b.uitgenodigd && (
                  <button onClick={() => opnieuw(b)} disabled={bezig}
                    data-testid={`beheerder-opnieuw-${b.id}`}
                    style={{ ...knopStijl, fontSize: 9, padding: "8px 10px" }}>Opnieuw</button>
                )}
                {!b.ik && (
                  <button onClick={() => verwijder(b)} disabled={bezig}
                    data-testid={`beheerder-weg-${b.id}`} style={wegStijl}>Weg</button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {data && !data.mail_actief && (
        <p data-testid="beheerders-mail-uit" style={{
          marginTop: 20, fontFamily: "var(--sans)", fontSize: 13, color: "#e8b0a4",
        }}>
          Let op: de maildienst is niet ingesteld, dus uitnodigingen worden niet verstuurd. U krijgt
          in dat geval een link te zien die u zelf kunt doorgeven.
        </p>
      )}
      <p style={{ marginTop: 20, fontFamily: "var(--sans)", fontSize: 13, opacity: 0.6, maxWidth: "64ch" }}>
        Zolang de site nog niet op het eigen webadres staat, weigert de maildienst onbekende
        ontvangers. Lukt de e-mail niet, dan verschijnt hier de uitnodigingslink; die kunt u zelf
        doorsturen. De uitnodiging blijft zeven dagen geldig.
      </p>

      <h2 style={{
        fontFamily: "var(--serif)", fontSize: 26, fontWeight: 400, margin: "56px 0 8px",
      }}>Mijn account</h2>
      <p style={{ fontFamily: "var(--sans)", fontSize: 14, opacity: 0.65, margin: "0 0 24px", maxWidth: "60ch" }}>
        Hiermee wijzigt u het e-mailadres waarmee u inlogt, en uw wachtwoord.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 32 }}>
        <form onSubmit={accountOpslaan} style={{ border: "1px solid var(--line)", padding: "22px 24px" }}>
          <label style={{ display: "block", marginBottom: 16 }}>
            <Label>Naam</Label>
            <input value={mijnNaam} onChange={(e) => setMijnNaam(e.target.value)} required
              data-testid="account-naam" style={invoerStijl} />
          </label>
          <label style={{ display: "block", marginBottom: 20 }}>
            <Label>E-mail</Label>
            <input type="email" value={mijnEmail} onChange={(e) => setMijnEmail(e.target.value)}
              required data-testid="account-email" style={invoerStijl} />
          </label>
          <button type="submit" disabled={bezig} data-testid="account-opslaan" style={{
            ...knopStijl, background: "var(--gold)", color: "var(--ink)", border: "none",
            padding: "13px 20px",
          }}>Opslaan</button>
        </form>
        <form onSubmit={wachtwoordOpslaan} style={{ border: "1px solid var(--line)", padding: "22px 24px" }}>
          <label style={{ display: "block", marginBottom: 16 }}>
            <Label>Huidig wachtwoord</Label>
            <input type="password" value={huidig} onChange={(e) => setHuidig(e.target.value)}
              required data-testid="account-huidig" style={invoerStijl} autoComplete="current-password" />
          </label>
          <label style={{ display: "block", marginBottom: 20 }}>
            <Label>Nieuw wachtwoord</Label>
            <input type="password" value={nieuw} onChange={(e) => setNieuw(e.target.value)}
              required minLength={8} data-testid="account-nieuw" style={invoerStijl}
              autoComplete="new-password" />
          </label>
          <button type="submit" disabled={bezig} data-testid="account-wachtwoord-opslaan" style={{
            ...knopStijl, padding: "13px 20px",
          }}>Wachtwoord wijzigen</button>
        </form>
      </div>
    </AdminShell>
  );
}
